/* @HEADER
 * @version 1.0.498 | 2026-08-24
 * @purpose ZVX-DEV-4971 — which project becomes active when the user switches organization.
 * @sidecar chooseProjectForWorkspace.ts.header.md
 * @layer app | @hex adapter | @ctx kanban
 * @public true
 * @edit careful
 */

import { resolveProjectOrgId, type OrgScopeProject } from './projectOrgScope.ts'

export interface ChooseProjectInput {
  /** Every project the device knows, in registry order. */
  projects: readonly (OrgScopeProject & { id: string })[]
  /** The organization just switched to. */
  workspaceId: string | null | undefined
  /** The project active before the switch. */
  activeProjectId?: string | null
  /** Resolves a project with no companyId — see projectOrgScope.ts. */
  personalWorkspaceId?: string | undefined
  /** Where the user was last time they were in each organization. */
  lastUsedByWorkspace?: Readonly<Record<string, string>>
}

/**
 * Pick the project the board and the left nav should show after an
 * organization switch.
 *
 * ## Why the switch alone was not enough
 *
 * `WorkspaceSwitcher` writes the active workspace id and fires
 * `workspace-switched`; it deliberately does not reload the page or touch
 * local data. `ProjectSwitcher` listens and re-filters the LIST of projects —
 * that part was fixed earlier. Nothing moved the ACTIVE project, and the left
 * nav is built from the active project, so switching organization left the
 * menu showing the previous organization's content. The operator reported it
 * as "switching organizations does not switch the menu".
 *
 * ## The rules, in the order they apply
 *
 * 1. If the active project already belongs to the organization, keep it —
 *    a switch must not shuffle the user off the board they were reading.
 * 2. Otherwise return where they were last time in this organization, if that
 *    project is still in it. A remembered project that has since moved out is
 *    ignored rather than resurrected.
 * 3. Otherwise the first project of that organization, in registry order.
 * 4. Otherwise `null` — the organization has nothing yet.
 *
 * Rule 4 is the one that matters most. Answering with a project from another
 * organization would put the user in front of foreign content while the
 * switcher says they are somewhere else: the same "shown one thing, acting on
 * another" shape as the ZVX-DEV-4919 leak, which put 199 duplicated tasks into
 * a colleague's organization. An empty organization must read as empty.
 */
export function chooseProjectForWorkspace(input: ChooseProjectInput): string | null {
  const { projects, workspaceId, activeProjectId, personalWorkspaceId, lastUsedByWorkspace } = input
  if (!workspaceId) return null

  const inWorkspace = (p: OrgScopeProject & { id: string }): boolean =>
    resolveProjectOrgId(p, personalWorkspaceId) === workspaceId

  const members = (projects ?? []).filter(inWorkspace)
  if (members.length === 0) return null

  if (activeProjectId && members.some((p) => p.id === activeProjectId)) return activeProjectId

  const remembered = lastUsedByWorkspace?.[workspaceId]
  if (remembered && members.some((p) => p.id === remembered)) return remembered

  return members[0].id
}
